import { useQuery, useMutation, useQueryClient, keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import api from '../utils/api.js'
import { invalidateCalendar } from './useCalendarEvents'
import type { User, Location, Space, Department, Paginated, QueryParams } from '../types/domain'

type Id = number | string

// Raw envelopes that some endpoints return either bare or wrapped in `{ data }`.
type SpacesResponse = Space[] | (Partial<Paginated<Space>> & { data?: Space[] })
interface ProductionSpacesResponse {
  spaces?: Space[]
  space_ids?: Id[]
  [key: string]: unknown
}

// ── Sites (top-level locations for global filter) ────────
export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: () => api.get<Location[]>('/locations/sites').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
}

// ── Departments & Locations ──────────────────────────────
export function useDepartments() {
  return useQuery({
    queryKey: ['departments'],
    queryFn: () => api.get<Department[]>('/departments').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  })
}

export function useLocations(params: QueryParams = {}) {
  return useQuery({
    queryKey: ['locations', params],
    queryFn: () => api.get<Location[] | { data?: Location[] }>('/locations', { params }).then(r => Array.isArray(r.data) ? r.data : r.data.data || []),
    staleTime: 5 * 60 * 1000,
  })
}

export function useCreateLocation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Location>) => api.post<Location>('/locations', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  })
}

export function useUpdateLocation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<Location> & { id: Id }) => api.put<Location>(`/locations/${id}`, data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  })
}

export function useDeleteLocation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: Id) => api.delete(`/locations/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
  })
}

// ── Users (admin) ────────────────────────────────────────
export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<User[] | { data?: User[] }>('/users').then(r => {
      const body = r.data
      return Array.isArray(body) ? body : (body.data || [])
    }),
  })
}

interface UserMutationInput extends Partial<User> {
  department_ids?: Array<number | string>
  primary_department_id?: number | string | null
}

export function useCreateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ department_ids, primary_department_id, ...data }: UserMutationInput) => {
      const result = await api.post<User>('/users', data).then(r => r.data)
      // Sync multi-department memberships if more than the primary
      if (Array.isArray(department_ids) && department_ids.length > 1 && result.id) {
        try {
          await api.put(`/users/${result.id}/departments`, { department_ids, primary_department_id })
        } catch (_) { /* endpoint may not exist pre-migration */ }
      }
      return result
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useUpdateUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, department_ids, primary_department_id, ...data }: UserMutationInput & { id: Id }) => {
      const result = await api.put<User>(`/users/${id}`, data).then(r => r.data)
      // Sync multi-department memberships if provided
      if (Array.isArray(department_ids)) {
        try {
          await api.put(`/users/${id}/departments`, { department_ids, primary_department_id })
        } catch (_) { /* endpoint may not exist pre-migration */ }
      }
      return result
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

export function useDeleteUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: Id) => api.delete(`/users/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  })
}

// ── Spaces ──────────────────────────────────────────────
export function useSpaces(params: QueryParams = {}) {
  return useQuery({
    queryKey: ['spaces', params],
    queryFn: () => api.get<SpacesResponse>('/spaces', { params }).then(r => r.data),
    placeholderData: keepPreviousData,
  })
}

export function useSpace(id: Id) {
  return useQuery({
    queryKey: ['space', id],
    queryFn: () => api.get<Space>(`/spaces/${id}`).then(r => r.data),
    enabled: !!id,
  })
}

export function useCreateSpace() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<Space>) => api.post<Space>('/spaces', data).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spaces'] }),
  })
}

export function useUpdateSpace() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: Partial<Space> & { id: Id }) => api.put<Space>(`/spaces/${id}`, data).then(r => r.data),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['spaces'] })
      qc.invalidateQueries({ queryKey: ['space', id] })
    },
  })
}

export function useDeleteSpace() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: Id) => api.delete(`/spaces/${id}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spaces'] }),
  })
}

// ── Production ↔ Space assignment (production_spaces join table) ──
export function useProductionSpaces(productionId: Id) {
  return useQuery({
    queryKey: ['production-spaces', productionId],
    queryFn: () => api.get<ProductionSpacesResponse>(`/productions/${productionId}/spaces`).then(r => r.data),
    enabled: !!productionId,
  })
}

export function useUpdateProductionSpaces() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ productionId, space_ids }: { productionId: Id; space_ids: Id[] }) =>
      api.put(`/productions/${productionId}/spaces`, { space_ids }).then(r => r.data),
    onSuccess: (_, { productionId }) =>
      qc.invalidateQueries({ queryKey: ['production-spaces', productionId] }),
  })
}

// Venue options for a form's space picker: the production's assigned spaces,
// falling back to every tenant space when the production has none assigned.
export function useVenueOptions(productionId: Id) {
  const prod = useProductionSpaces(productionId)
  const all = useSpaces({ limit: 500 })
  const prodSpaces = prod.data?.spaces ?? []
  const allData = all.data
  const allSpaces = (Array.isArray(allData) ? allData : allData?.data) ?? []
  return prodSpaces.length ? prodSpaces : allSpaces
}

interface SpaceGroup {
  key: Id
  label: string
  spaces: Space[]
}

// Site-grouped space options for form dropdowns. Returns spaces grouped by
// their site (locations.name, joined onto each space row as `location_name`),
// each group + its spaces sorted by name. Spaces with no site land in a
// trailing label-less group so the dropdown still offers them.
//   { groups: [{ key, label, spaces: [{id,name,location_id,...}] }],
//     byId: Map<id, space>, isLoading }
// Pass a productionId to scope to that production's assigned spaces (the set
// falls back to every tenant space when the production has none assigned).
export function useSpaceOptions(productionId: Id) {
  const all = useSpaces({ limit: 500 })
  const prod = useProductionSpaces(productionId)
  const allData = all.data
  const allSpaces = (Array.isArray(allData) ? allData : allData?.data) ?? []
  const prodIds = productionId ? (prod.data?.space_ids ?? []) : null
  const spaces = prodIds && prodIds.length
    ? allSpaces.filter((s) => prodIds.includes(s.id))
    : allSpaces

  const bySite = new Map<Id, SpaceGroup>()
  for (const s of spaces) {
    const key = s.location_id ?? 0
    if (!bySite.has(key)) {
      bySite.set(key, { key, label: s.location_name || '', spaces: [] })
    }
    bySite.get(key)!.spaces.push(s)
  }
  const groups = [...bySite.values()].sort((a, b) => {
    if (!a.label) return 1
    if (!b.label) return -1
    return a.label.localeCompare(b.label)
  })
  for (const g of groups) g.spaces.sort((a, b) => String(a.name).localeCompare(String(b.name)))

  const byId = new Map(spaces.map((s) => [s.id, s]))
  return {
    groups,
    byId,
    isLoading: all.isLoading || (!!productionId && prod.isLoading),
  }
}

export function useAllSpaceBookings(params: QueryParams = {}) {
  return useQuery({
    queryKey: ['space-bookings-all', params],
    queryFn: () => api.get<unknown>('/spaces/bookings/all', { params }).then(r => r.data),
    enabled: !!params.from && !!params.to,
  })
}

export function useSpaceBookings(spaceId: Id, params: QueryParams = {}) {
  return useQuery({
    queryKey: ['space-bookings', spaceId, params],
    queryFn: () => api.get<unknown>(`/spaces/${spaceId}/bookings`, { params }).then(r => r.data),
    enabled: !!spaceId,
  })
}

export function useCreateSpaceBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ spaceId, ...data }: { spaceId: Id; [key: string]: unknown }) => api.post(`/spaces/${spaceId}/bookings`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['space-bookings'] })
      qc.invalidateQueries({ queryKey: ['spaces'] })
      // Bookings surface as 'hold' rows on the Calendar Hub.
      invalidateCalendar(qc)
    },
  })
}

export function useDeleteSpaceBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: Id) => api.delete(`/spaces/bookings/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['space-bookings'] })
      qc.invalidateQueries({ queryKey: ['spaces'] })
      // Bookings surface as 'hold' rows on the Calendar Hub.
      invalidateCalendar(qc)
    },
  })
}

// ── Space files (photos & documents) ─────────────────────
export function useSpaceFiles(spaceId: Id, kind?: string | null) {
  return useQuery({
    queryKey: ['space-files', String(spaceId), kind || 'all'],
    queryFn: () => api.get<unknown[]>(`/spaces/${spaceId}/files`, { params: kind ? { kind } : {} })
      .then(r => r.data),
    enabled: !!spaceId,
  })
}

export function useUploadSpaceFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ spaceId, file, kind, doc_type, title }: { spaceId: Id; file: File | Blob; kind: string; doc_type?: string; title?: string }) => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('kind', kind)
      if (doc_type) fd.append('doc_type', doc_type)
      if (title) fd.append('title', title)
      return api.post(`/spaces/${spaceId}/files/upload`, fd).then(r => r.data)
    },
    onSuccess: (_data, { spaceId }) => {
      qc.invalidateQueries({ queryKey: ['space-files', String(spaceId)] })
      qc.invalidateQueries({ queryKey: ['spaces'] })
    },
  })
}

export function useLinkSpaceDocument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ spaceId, external_url, doc_type, title }: { spaceId: Id; external_url: string; doc_type?: string; title?: string }) =>
      api.post(`/spaces/${spaceId}/files/link`, { external_url, doc_type, title })
        .then(r => r.data),
    onSuccess: (_data, { spaceId }) => {
      qc.invalidateQueries({ queryKey: ['space-files', String(spaceId)] })
    },
  })
}

export function useUpdateSpaceFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ fileId, ...patch }: { fileId: Id; spaceId?: Id; [key: string]: unknown }) =>
      api.patch(`/spaces/files/${fileId}`, patch).then(r => r.data),
    onSuccess: (_data, { spaceId }) => {
      qc.invalidateQueries({ queryKey: ['space-files', String(spaceId)] })
      qc.invalidateQueries({ queryKey: ['spaces'] })
    },
  })
}

export function useDeleteSpaceFile() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ fileId }: { fileId: Id; spaceId?: Id }) => api.delete(`/spaces/files/${fileId}`).then(r => r.data),
    onSuccess: (_data, { spaceId }) => {
      qc.invalidateQueries({ queryKey: ['space-files', String(spaceId)] })
      qc.invalidateQueries({ queryKey: ['spaces'] })
    },
  })
}

// ── Keyset infinite scroll ───────────────────────────────
export function useUsersInfinite(params: QueryParams = {}) {
  return useInfiniteQuery({
    queryKey: ['users', 'infinite', params],
    queryFn: ({ pageParam }) =>
      api.get<Paginated<User>>('/users', { params: { ...params, cursor: pageParam ?? '', limit: 50 } }).then(r => r.data),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 30_000,
  })
}
